// Browser Friendly version of FetchLib (copied from Kai codebase)
import { deepStripNullish, isJsonObject, JsonValue, log, networkFailure, object, objectHasKeys, secondsPer, upperSnakeCase } from '@art-suite/art-core-ts';
import { ZodLessInferOutput, zodLessParse, ZodLessSchema } from '@kaiizen/zod-less';
import { ErrorWithStatus } from './ErrorWithStatus';
import { HttpMethodUppercase } from './types';

const DEFAULT_TIMEOUT_SECONDS = secondsPer.minute * 5;

/**************************************************
 * SimplifiedRequest
 *
 * SimplifiedRequest represents the data, key (the rest of the url de-url-encoded) and query params
 * In particular, the query params are merged in as first-level properties.
 *
 * Example: http://foo.com/my-request-type/abc123?foo=bar&baz=qux
 * Would be represented as:
 * {
 *   data: {},
 *   key: 'abc123',
 *   foo: 'bar',
 *   baz: 'qux'
 * }
 **************************************************/
export type OutgoingSimplifiedRequest = {
  payload?: JsonValue;
  key?: string;
  query?: Record<string, string | number | boolean>;
  bearer?: string | null;
  headers?: Record<string, string>; // Optional headers for the request
  signal?: AbortSignal;
};

export type OutgoingSimplifiedFormRequest = OutgoingSimplifiedRequest & {
  payload: Record<string, string>;
  files?: Record<string, FetchUploadableBinaryData>;
};

export const enrichFetchError = async (error: any, url: string) => {
  if (error?.response?.status) throw error;

  let detailedErrorMessage = [
    /failed.*fetch|fetch.*failed/i.test(error.message) ? 'Fetch failed' : `Fetch failed: ${error.message}`,
    `url: ${JSON.stringify(url)}`,
  ];

  error.message = detailedErrorMessage.join(', ');
  throw error;
};

const JSON_HEADERS = { 'Content-Type': 'application/json' };

const responseToJson = (response: Response, body: string) => {
  try {
    const contentType = response.headers.get('Content-Type') || '';
    if (/application\/json/.test(contentType)) return JSON.parse(body) as JsonValue;
    // else if (/application\/xml|text\/xml/.test(contentType)) return parseXmlToJson(body);
    else throw new Error(`Unsupported response type: ${contentType}`);
  } catch (error: any) {
    throw new Error(
      `Error parsing response ${getResponseType(response)} error: ${error.message.split('\n')[0]}, response:\n${body}`,
    );
  }
};

const responseToJsonOrUnparsed = (response: Response, body: string) => {
  try {
    return responseToJson(response, body);
  } catch (error) {
    return body;
  }
};

type FetchResponseProcessor<T> = (response: Response) => Promise<T>;

const getResponseType = (response: Response) => upperSnakeCase((response.headers.get('Content-Type') || 'application/unknown').split(';')[0].split('/')[1]);

const processJsonFetchResponse: FetchResponseProcessor<JsonValue> = (response: Response) => response.text().then(body => responseToJson(response, body));

const processBinaryFetchResponse: FetchResponseProcessor<Blob> = (response: Response) => response.blob();

const urlWithQueryParams = (url: string, request?: OutgoingSimplifiedRequest) => {
  if (!request) return url;
  if (request?.key) url = `${url}/${encodeURIComponent(request.key)}`;
  const queryParams = request.query ? deepStripNullish(request.query) : {};
  if (objectHasKeys(queryParams)) {
    const queryString = new URLSearchParams(object(queryParams, value => `${value}`)).toString();
    if (queryString) url = `${url}?${queryString}`;
  }
  return url;
};

const logIfVerbose = (verbose: boolean, ...messages: any[]) => (verbose ? log.unquoted(...messages) : undefined);

const jsonToFormData = (json: { [key: string]: any }): FormData => {
  const formData = new FormData();
  Object.keys(json).forEach(key => formData.append(key, json[key]));
  return formData;
};

const getAuthorizationHeader = (request: OutgoingSimplifiedRequest | undefined) => request?.bearer ? { authorization: `Bearer ${request.bearer}` } : undefined;

// TODO - the args to this helper function seem more complex than they need to be
const executeFetch = async <T, NullOn404 extends boolean>(
  method: string,
  url: string,
  body: any,
  additionalHeaders: Record<string, string>,
  request: OutgoingSimplifiedRequest | undefined,
  responseProcessor: FetchResponseProcessor<T>,
  returnNullOn404: NullOn404,
  verbose: boolean,
  timeoutSeconds: number = DEFAULT_TIMEOUT_SECONDS,
): Promise<NullOn404 extends false ? T : T | null> => {
  const fetchUrl = urlWithQueryParams(url, request);

  // manually create a timeout controller and signal
  const timeoutAbortController = new AbortController();
  const timeoutId = setTimeout(() => timeoutAbortController.abort(), timeoutSeconds * 1000);

  const fetchParams = {
    method,
    body,
    headers: {
      ...request?.headers,
      ...getAuthorizationHeader(request),
      ...additionalHeaders,
    },
    signal: request?.signal ? AbortSignal.any([request.signal, timeoutAbortController.signal]) : timeoutAbortController.signal,
  };
  logIfVerbose(verbose, { fetchJson: { fetchUrl, fetchParams } });

  if (verbose) {
    const curlCommand = `curl -X ${method} ${fetchUrl} ${
      Object.entries(fetchParams.headers)
        .map(([key, value]) => `-H "${key}: ${value}"`)
        .join(' ')
    } ${fetchParams.body ? `-d '${fetchParams.body}'` : ''}`;
    logIfVerbose(verbose, `Equivalent curl command: ${curlCommand}`);
  }

  try {
    // @ts-ignore because typescript is dumb - DONT REMOVE the await - otherwise the catch below will not work
    return await fetch(fetchUrl, fetchParams).then(async (response: Response) => {
      if (!response.ok) {
        if (returnNullOn404 && response.status === 404) return null;
        const text = await response.text();
        const payload = await responseToJsonOrUnparsed(response, text);
        const remoteMessage: any = isJsonObject(payload) ? payload.error || payload.message : '';

        const error = new ErrorWithStatus(
          `FetchLib ${getResponseType(response)} error ${response.status} ${response.statusText}: ${method} ${JSON.stringify(url)} ${
            typeof remoteMessage === 'string'
              ? remoteMessage
              : typeof remoteMessage?.message === 'string'
              ? remoteMessage.message
              : JSON.stringify(remoteMessage)
          }`,
          response.status,
          {
            request,
            response,
            payload,
            remoteMessage,
          },
        );
        error.response = response;
        throw error;
      }

      return responseProcessor(response);
    });
  } catch (error: any) {
    // DON'T REMOVE the await - otherwise we'll throw the promise instead of the error
    if (!(error instanceof ErrorWithStatus)) {
      throw new ErrorWithStatus(
        `FetchLib network failure: ${method} ${JSON.stringify(url)} ${error.message}`,
        networkFailure,
        { request, response: error.response, payload: error.payload },
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

const fetchAny = async <T, NullOn404 extends boolean>(
  method: HttpMethodUppercase,
  url: string,
  processor: FetchResponseProcessor<T>,
  request: OutgoingSimplifiedRequest | undefined,
  returnNullOn404: NullOn404,
  verbose = false,
  timeoutSeconds?: number,
): Promise<NullOn404 extends false ? T : T | null> =>
  executeFetch(
    method,
    url,
    request?.payload ? JSON.stringify(request?.payload) : undefined,
    JSON_HEADERS,
    request,
    processor,
    returnNullOn404,
    verbose,
    timeoutSeconds,
  );

export const fetchJson = (
  method: HttpMethodUppercase,
  url: string,
  request?: OutgoingSimplifiedRequest,
  returnNullOn404 = false,
  verbose = false,
  timeoutSeconds?: number,
) => fetchAny(method, url, processJsonFetchResponse, request, returnNullOn404, verbose, timeoutSeconds);

const fetchBinary = (
  method: HttpMethodUppercase,
  url: string,
  request?: OutgoingSimplifiedRequest,
  returnNullOn404 = false,
  verbose = false,
) => fetchAny(method, url, processBinaryFetchResponse, request, returnNullOn404, verbose);

export const getBinary = (url: string, request?: OutgoingSimplifiedRequest): Promise<Blob> => fetchBinary('GET', url, request) as Promise<Blob>;

export const postBinary = (url: string, request?: OutgoingSimplifiedRequest): Promise<Blob> => fetchBinary('POST', url, request) as Promise<Blob>;

export const getJson = (url: string, request?: OutgoingSimplifiedRequest, returnNullOn404 = false, verbose = false) =>
  fetchJson('GET', url, request, returnNullOn404, verbose);
export const postJson = (url: string, request?: OutgoingSimplifiedRequest, returnNullOn404 = false, verbose = false) =>
  fetchJson('POST', url, request, returnNullOn404, verbose);
export const patchJson = (url: string, request?: OutgoingSimplifiedRequest, returnNullOn404 = false, verbose = false) =>
  fetchJson('PATCH', url, request, returnNullOn404, verbose);
export const deleteJson = (url: string, request?: OutgoingSimplifiedRequest, returnNullOn404 = false, verbose = false) =>
  fetchJson('DELETE', url, request, returnNullOn404, verbose);

export const getJsonResponseParsed = <T extends ZodLessSchema>(
  url: string,
  request: OutgoingSimplifiedRequest,
  responseSchema: T,
): Promise<ZodLessInferOutput<T>> => getJson(url, request).then(json => zodLessParse(responseSchema as any, json)) as any;
export const postJsonResponseParsed = <T extends ZodLessSchema>(
  url: string,
  request: OutgoingSimplifiedRequest,
  responseSchema: T,
): Promise<ZodLessInferOutput<T>> => postJson(url, request).then(json => zodLessParse(responseSchema as any, json)) as any;
export const patchJsonResponseParsed = <T extends ZodLessSchema>(
  url: string,
  request: OutgoingSimplifiedRequest,
  responseSchema: T,
): Promise<ZodLessInferOutput<T>> => patchJson(url, request).then(json => zodLessParse(responseSchema as any, json)) as any;
export const deleteJsonResponseParsed = <T extends ZodLessSchema>(
  url: string,
  request: OutgoingSimplifiedRequest,
  responseSchema: T,
): Promise<ZodLessInferOutput<T>> => deleteJson(url, request).then(json => zodLessParse(responseSchema as any, json)) as any;

export type FetchUploadableBinaryData = Blob;

const isFetchUploadableBinaryData = (value: unknown): value is FetchUploadableBinaryData => value instanceof Blob;

/** The request's payload fields and files as FormData. A file-only upload has no payload. */
const buildFormData = (request: OutgoingSimplifiedFormRequest): FormData => {
  const body = jsonToFormData(request.payload ?? {});
  if (request.files) {
    for (const [fieldName, fileData] of Object.entries(request.files)) {
      if (isFetchUploadableBinaryData(fileData)) body.append(fieldName, fileData);
    }
  }
  return body;
};

export const postFormData = async (
  url: string,
  request: OutgoingSimplifiedFormRequest,
  verbose = false,
  timeoutSeconds?: number,
  onProgress?: (progress: number) => void,
) => {
  const body = buildFormData(request);

  if (onProgress) {
    return postFormDataWithXhr(url, body, request, timeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS, onProgress);
  }

  return executeFetch('POST', url, body, {}, request, processJsonFetchResponse, false, verbose, timeoutSeconds);
};

/** Upload FormData via XMLHttpRequest for upload progress support. */
const postFormDataWithXhr = (
  url: string,
  body: FormData,
  request: OutgoingSimplifiedFormRequest,
  timeoutSeconds: number,
  onProgress: (progress: number) => void,
): Promise<JsonValue> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', urlWithQueryParams(url, request));
    xhr.timeout = timeoutSeconds * 1000;

    const authHeader = getAuthorizationHeader(request);
    if (authHeader?.authorization) xhr.setRequestHeader('authorization', authHeader.authorization);
    if (request.headers) {
      for (const [key, value] of Object.entries(request.headers)) xhr.setRequestHeader(key, value);
    }

    if (request.signal) {
      if (request.signal.aborted) {
        reject(new ErrorWithStatus(`FetchLib aborted: POST ${JSON.stringify(url)}`, networkFailure, { request }));
        return;
      }
      request.signal.addEventListener('abort', () => xhr.abort());
    }

    xhr.upload.onprogress = event => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          resolve(xhr.responseText as any);
        }
      } else {
        const payload = (() => {
          try {
            return JSON.parse(xhr.responseText);
          } catch {
            return xhr.responseText;
          }
        })();
        const remoteMessage: any = isJsonObject(payload) ? payload.error || payload.message : '';
        reject(
          new ErrorWithStatus(
            `FetchLib XHR error ${xhr.status} ${xhr.statusText}: POST ${JSON.stringify(url)} ${
              typeof remoteMessage === 'string' ? remoteMessage : JSON.stringify(remoteMessage)
            }`,
            xhr.status,
            { request, payload, remoteMessage },
          ),
        );
      }
    };

    xhr.onerror = () =>
      reject(
        new ErrorWithStatus(
          `FetchLib network failure: POST ${JSON.stringify(url)}`,
          networkFailure,
          { request },
        ),
      );

    xhr.ontimeout = () =>
      reject(
        new ErrorWithStatus(
          `FetchLib timeout (${timeoutSeconds}s): POST ${JSON.stringify(url)}`,
          networkFailure,
          { request },
        ),
      );

    xhr.onabort = () =>
      reject(
        new ErrorWithStatus(
          `FetchLib aborted: POST ${JSON.stringify(url)}`,
          networkFailure,
          { request },
        ),
      );

    xhr.send(body);
  });

export const postFormDataJsonResponseParsed = <T extends ZodLessSchema>(
  url: string,
  request: OutgoingSimplifiedFormRequest,
  responseSchema: T,
  timeoutSeconds?: number,
  onProgress?: (progress: number) => void,
): Promise<ZodLessInferOutput<T>> =>
  postFormData(url, request, false, timeoutSeconds, onProgress).then(json => zodLessParse(responseSchema as any, json)) as any;
